/**
 * Public, unauthenticated pages: landing, pricing, privacy and terms.
 *
 * Plan limits are rendered from the shared plan catalogue so the marketing copy
 * cannot drift away from the values the Worker actually enforces.
 */

import { FREE_ENTITLEMENTS, PRO_ENTITLEMENTS } from '@core/plans';
import { Link } from '../lib/router';
import { Alert } from '../components/ui';
import { formatBytes } from '../lib/format';
import { useSession } from '../lib/session';
import { useRouter } from '../lib/router';

/* -------------------------------------------------------------------------- */
/* Landing                                                                     */
/* -------------------------------------------------------------------------- */

export function LandingPage() {
  const { status } = useSession();
  const signedIn = status === 'signed_in';

  return (
    <>
      <section className="hero">
        <p className="eyebrow">Paste the failure. Get the fix.</p>
        <h1>Turn an API failure into a corrected request and a clear incident report.</h1>
        <p className="hero__lead">
          Paste a curl command, an HTTP request or response, a webhook payload, a JSON or YAML
          document, or a stack trace. API Rescue Desk identifies the likely cause, runs
          deterministic checks, validates against your OpenAPI specification, and hands back a
          corrected example, a verification checklist and a support-ready summary.
        </p>
        <div className="row hero__actions">
          <Link className="button button--primary" to={signedIn ? '/app' : '/signin'}>
            {signedIn ? 'Open the desk' : 'Sign in with Google'}
          </Link>
          <Link className="button" to="/pricing">
            See pricing
          </Link>
        </div>
        <p className="kbd-hint" style={{ marginTop: 12 }}>
          Google sign-in is required. Anonymous diagnoses are not accepted.
        </p>
      </section>

      <section style={{ marginTop: 48 }}>
        <h2>How it works</h2>
        <ol className="steps-inline">
          <li>
            <strong>Paste the failure.</strong>
            <p>
              Raw is best: the curl command, the full HTTP response with its status line and
              headers, or the provider error text. The classifier can also be set manually if it
              guesses wrong.
            </p>
          </li>
          <li>
            <strong>Secrets are redacted before anything is stored.</strong>
            <p>
              Redaction runs in the browser for an instant preview and again on the server before
              the text is persisted or sent to a model. The result is always shown to you with a
              review warning.
            </p>
          </li>
          <li>
            <strong>Deterministic checks run first.</strong>
            <p>
              Authentication headers, JSON validity, content types, URL shape, transport errors,
              webhook signatures, stack frames and OpenAPI contract violations are all evaluated
              without an AI provider. The report works with no key configured at all.
            </p>
          </li>
          <li>
            <strong>An AI provider adds colour, never facts.</strong>
            <p>
              Bring your own OpenAI-compatible key, use the built-in deterministic analysis, or use
              a platform-managed provider if the operator enables one. Model output is
              schema-checked and clearly labelled.
            </p>
          </li>
        </ol>
      </section>

      <section style={{ marginTop: 48 }}>
        <h2>What it recognises</h2>
        <div className="grid grid--3">
          <div className="card feature">
            <h3>Requests and responses</h3>
            <p>
              curl commands, raw HTTP/1.1 messages, request-only or response-only pastes, and plain
              provider error messages.
            </p>
          </div>
          <div className="card feature">
            <h3>Payloads</h3>
            <p>
              JSON and YAML documents, webhook envelopes including signature and timestamp headers,
              and double-encoded fields.
            </p>
          </div>
          <div className="card feature">
            <h3>Runtime failures</h3>
            <p>
              Node, browser, Python, JVM, .NET, Ruby and Go stack traces, with the top meaningful
              frame called out.
            </p>
          </div>
          <div className="card feature">
            <h3>OpenAPI 3.x</h3>
            <p>
              Operation matching including path parameters, required headers and query parameters,
              content types, required body fields, types, enums and documented status codes.
            </p>
          </div>
          <div className="card feature">
            <h3>Evidence you can audit</h3>
            <p>
              Every cause is labelled confirmed, likely or speculative, and states the evidence it
              came from. Nothing is presented as a test result.
            </p>
          </div>
          <div className="card feature">
            <h3>Support-ready output</h3>
            <p>
              A copyable ticket summary, a verification checklist and Markdown export (JSON export
              on Pro) so the fix and the write-up happen together.
            </p>
          </div>
        </div>
      </section>

      <section style={{ marginTop: 48 }}>
        <Alert tone="warning" title="What this product deliberately does not do">
          <ul className="plain-list">
            <li>
              It never executes your curl command, request or payload, and it never runs a shell.
            </li>
            <li>It never fetches a URL taken from your input, not even for an OpenAPI document.</li>
            <li>It never stores a bring-your-own AI key, and it never logs raw input.</li>
            <li>
              It never claims a fix worked. Findings come from the text you supplied, and redaction
              is best-effort — always review the sanitized text before sharing it.
            </li>
          </ul>
        </Alert>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Pricing                                                                     */
/* -------------------------------------------------------------------------- */

export function PricingPage() {
  const { status } = useSession();
  const { navigate } = useRouter();
  const signedIn = status === 'signed_in';

  return (
    <>
      <div className="page-head">
        <h1>Pricing</h1>
        <p>
          Built to run inside free tiers. One developer can use the Free plan indefinitely; Pro is
          for people who diagnose failures every day and need to share them.
        </p>
      </div>

      <div className="grid grid--2">
        <div className="plan">
          <h2>{FREE_ENTITLEMENTS.displayName}</h2>
          <div className="plan__price">
            $0 <span>forever</span>
          </div>
          <ul>
            <li>Google account required</li>
            <li>{FREE_ENTITLEMENTS.monthlyDiagnoses} diagnoses per calendar month</li>
            <li>Maximum input size of {formatBytes(FREE_ENTITLEMENTS.maxInputBytes)}</li>
            <li>One saved OpenAPI specification</li>
            <li>Diagnosis history retained for {FREE_ENTITLEMENTS.historyRetentionDays} days</li>
            <li>Secret redaction with a review warning</li>
            <li>Corrected request generation</li>
            <li>Markdown report export</li>
            <li className="is-excluded">No shareable incident reports</li>
            <li className="is-excluded">Structural OpenAPI checks only</li>
          </ul>
          <div className="plan__footer">
            <button
              type="button"
              className="button"
              onClick={() => navigate(signedIn ? '/app' : '/signin')}
            >
              {signedIn ? 'Open the desk' : 'Start free'}
            </button>
          </div>
        </div>

        <div className="plan plan--featured">
          <h2>
            {PRO_ENTITLEMENTS.displayName} <span className="badge badge--pro">Subscription</span>
          </h2>
          <div className="plan__price">
            ${PRO_ENTITLEMENTS.priceMonthlyUsd?.toFixed(2)} <span>per month</span>
          </div>
          <ul>
            <li>{PRO_ENTITLEMENTS.monthlyDiagnoses} diagnoses per calendar month</li>
            <li>Maximum input size of {formatBytes(PRO_ENTITLEMENTS.maxInputBytes)}</li>
            <li>Up to {PRO_ENTITLEMENTS.maxSavedSpecs} saved OpenAPI specifications</li>
            <li>Persistent diagnosis history</li>
            <li>Full OpenAPI-aware request and response validation</li>
            <li>Shareable private incident reports with revocation and expiry</li>
            <li>Markdown and JSON export</li>
            <li>Priority processing flag in the entitlement model</li>
          </ul>
          <div className="plan__footer">
            <button
              type="button"
              className="button button--primary"
              onClick={() => navigate('/app/billing')}
            >
              {signedIn ? 'Manage subscription' : 'Sign in to upgrade'}
            </button>
            <p className="small faint" style={{ marginTop: 8 }}>
              Checkout, invoicing and cancellation run through Stripe. Access is only granted after
              a signature-verified webhook confirms the subscription.
            </p>
          </div>
        </div>
      </div>

      <section style={{ marginTop: 40 }}>
        <h2>Questions worth answering up front</h2>
        <div className="stack">
          <details>
            <summary>Do you store my API keys?</summary>
            <p>
              No. A bring-your-own AI key lives in browser memory for the session and is sent only
              to the protected diagnosis endpoint over HTTPS for that one request. It is never
              written to the database, never logged, and never included in an error message.
              Separately, secrets inside the text you paste are replaced with placeholders before
              anything is stored.
            </p>
          </details>
          <details>
            <summary>Is the plan I see in the browser what I get?</summary>
            <p>
              The browser only renders the plan. Every quota, ownership check and entitlement is
              re-resolved on the server from the database, and the subscription row itself is
              written only by verified Stripe webhooks.
            </p>
          </details>
          <details>
            <summary>What happens to my history on the Free plan?</summary>
            <p>
              Free-plan diagnoses carry an expiry of {FREE_ENTITLEMENTS.historyRetentionDays} days
              and are removed by a scheduled cleanup. Pro history is kept until you delete it or
              delete your account.
            </p>
          </details>
          <details>
            <summary>Can I use it without an AI provider?</summary>
            <p>
              Yes. The deterministic engine produces a full report with no external service. Adding
              a provider enriches the narrative; it never replaces the checks.
            </p>
          </details>
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Privacy                                                                     */
/* -------------------------------------------------------------------------- */

export function PrivacyPage() {
  return (
    <div className="container-narrow">
      <div className="page-head">
        <h1>Privacy</h1>
        <p>What API Rescue Desk stores, why, and for how long.</p>
      </div>

      <section className="card">
        <h2>What is stored</h2>
        <ul className="plain-list">
          <li>
            <strong>Profile:</strong> your Google account identifier, email address, display name
            and avatar URL, so the application can show you your own data.
          </li>
          <li>
            <strong>Diagnoses:</strong> the redacted (“sanitized”) version of what you pasted, the
            generated report, and non-sensitive metadata such as the detected input type.
          </li>
          <li>
            <strong>Saved OpenAPI specifications:</strong> a normalized copy of the document you
            chose to save. Nothing is stored when you only paste a specification for one diagnosis.
          </li>
          <li>
            <strong>Usage counters:</strong> how many diagnoses were used in the current calendar
            month, per account.
          </li>
          <li>
            <strong>Subscription state:</strong> Stripe customer and subscription identifiers, the
            status, and the current period end. Card details never reach this application.
          </li>
          <li>
            <strong>Share links:</strong> a cryptographic hash of the token. The token itself is
            shown once and cannot be recovered.
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>What is never stored</h2>
        <ul className="plain-list">
          <li>Your bring-your-own AI API key.</li>
          <li>Raw, un-redacted input.</li>
          <li>Card numbers, CVV codes or bank details.</li>
          <li>
            Uploaded files: specifications are parsed into structured data, the file is discarded.
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>Retention</h2>
        <ul className="plain-list">
          <li>
            Free plan: diagnoses expire {FREE_ENTITLEMENTS.historyRetentionDays} days after creation
            and are removed by a daily cleanup job. Pro plan: history is kept until you delete it or
            delete your account. Share links expire on the date you choose, and can be revoked at
            any time.
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>Account deletion</h2>
        <p>
          Deleting your account hard-deletes your profile, diagnoses, saved specifications, usage
          periods, subscription cache and share links, and then removes the authentication record.
          The deletion is idempotent, so retrying a failed request is safe.
        </p>
      </section>

      <section className="card">
        <h2>Honest limits</h2>
        <p>
          Redaction is a best-effort pattern and structure matcher. It will not catch every secret,
          and it cannot know your organisation's naming conventions. Always review the sanitized
          text shown after a diagnosis, and never paste production credentials into any third-party
          tool without checking what will be retained.
        </p>
      </section>

      <section className="card">
        <h2>Third parties</h2>
        <ul className="plain-list">
          <li>Supabase provides authentication and PostgreSQL storage.</li>
          <li>Stripe processes subscription payments and hosts the billing portal.</li>
          <li>Cloudflare serves the frontend and runs the protected API.</li>
          <li>
            An AI provider is only contacted if you supply a key, or if the operator has enabled a
            platform-managed provider. In that case the redacted text and the extracted evidence —
            not your raw paste — are sent.
          </li>
        </ul>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Terms                                                                       */
/* -------------------------------------------------------------------------- */

export function TermsPage() {
  return (
    <div className="container-narrow">
      <div className="page-head">
        <h1>Terms of use</h1>
        <p>A short, plain-language summary of the terms for this deployment.</p>
      </div>

      <section className="card">
        <h2>The service</h2>
        <p>
          API Rescue Desk analyses text you submit describing a failed API interaction and produces
          an advisory report. It does not connect to your systems, does not execute your requests,
          and does not modify anything on your behalf.
        </p>
      </section>

      <section className="card">
        <h2>No warranty, no verification</h2>
        <p>
          Findings are derived from the text you provide. They are not test results and are not
          guaranteed to be correct or complete. You are responsible for reviewing any suggested
          change before applying it, and for deciding whether the service is appropriate for the
          data you paste into it.
        </p>
      </section>

      <section className="card">
        <h2>Acceptable use</h2>
        <ul className="plain-list">
          <li>Do not use the service to process data you are not permitted to share.</li>
          <li>Do not attempt to bypass quotas, authentication or rate limits.</li>
          <li>Do not use the service to attack, probe or scan systems you do not own.</li>
        </ul>
      </section>

      <section className="card">
        <h2>Subscriptions</h2>
        <p>
          Paid subscriptions are billed by Stripe on a recurring monthly basis and can be cancelled
          at any time through the Stripe customer portal. Access continues until the end of the
          period you have already paid for. Removing access for non-payment is applied by the same
          signature-verified webhook path that grants it.
        </p>
      </section>

      <section className="card">
        <h2>Your content</h2>
        <p>
          You keep ownership of anything you submit. You grant this deployment only the permission
          needed to store, process and display it back to you (and, for share links you create, to
          the people holding the link).
        </p>
      </section>

      <section className="card">
        <h2>Termination</h2>
        <p>
          You can delete your account at any time from the Account page. Deleting your account
          removes your application data as described on the Privacy page.
        </p>
      </section>
    </div>
  );
}
